import { Intent as BlueprintIntent } from '@blueprintjs/core';
import { OverflowMenuHorizontal } from '@carbon/icons-react';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Body,
  Link,
  Menu,
  MenuItem,
  MenuItems,
  Tooltip,
} from 'vet-bones/design-system/components';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { useAppDispatch } from 'src/ui/app/hooks';
import { withConfirmationDs } from 'src/ui/components/withConfirmationDs';
import {
  PORTAL_ENDPOINT_TABLE_CONFIRMATION_YES,
  PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU,
  PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM,
  PORTAL_ENDPOINT_TABLE_ROW_NAME,
} from 'src/ui/constants/testIds';
import { SduMenuActions } from 'src/ui/containers/endpoints/SduMenuActions';
import { getEndpointAuthType } from 'src/ui/containers/endpoints/utils/getAuthType';
import { getEndpointUrl } from 'src/ui/containers/endpoints/utils/getEndpointUrl';
import { getUserInfo } from 'src/ui/containers/endpoints/utils/getUserItem';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { useDownloadLogs } from 'src/ui/containers/endpoints/utils/useDownloadLogs';
import { useHasReadLogsPermissions } from 'src/ui/containers/endpoints/utils/useHasReadLogsPermissions';
import { useIsSduSupported } from 'src/ui/containers/endpoints/utils/useIsSduSupported';
import { editConnection } from 'src/ui/features/connection';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  useGetStripeSessionUrlMutation,
  useRemoveMySharedConnectionMutation,
} from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import { useEndpointVersionGate } from 'src/ui/hooks/endpoint';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { useValidateConnection } from 'src/ui/hooks/validateConnection';
import * as copy from 'src/ui/templates/copy';
import { buildRoute } from 'src/ui/utils/buildRoute';
import { redirectToUrl } from 'src/ui/utils/window';

export interface PortalEndpointRowProps {
  connection: Connection;
}

export const PortalEndpointRow: React.VFC<{ connection: Connection }> = ({
  connection,
}) => {
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { data: profile } = useProfile();
  const validateConnection = useValidateConnection();
  const invalidateConnection = useInvalidateConnection(
    '',
    connection?.id,
    connection?.index
  );

  const { mutate: getStripeSessionUrl } = useGetStripeSessionUrlMutation(
    graphQLClient,
    {
      onSuccess: (data) => {
        redirectToUrl(data?.getStripeSessionUrl?.url || '/');
      },
    }
  );

  const {
    mutate: removeMySharedConnection,
  } = useRemoveMySharedConnectionMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data) => {
      invalidateConnection();

      if (data.removeMySharedConnection?.error) {
        dispatch(
          queueNotification(
            data.removeMySharedConnection.error ||
              copy.components.endpoints.endpointTable.notifications
                .deleteSharedEndpoint.error
          )
        );
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.endpoints.endpointTable.notifications
              .deleteSharedEndpoint.success,
        })
      );
    },
  });

  const isDownloadLogsSupported = useEndpointVersionGate(connection, '11.1.0');
  const isDownloadVoiceboxReportSupported = useEndpointVersionGate(
    connection,
    '10.0.0'
  );
  const isSduSupported = useIsSduSupported(connection);
  const canReadLogs = useHasReadLogsPermissions(
    connection,
    isDownloadLogsSupported ||
      isDownloadVoiceboxReportSupported ||
      isSduSupported
  );

  const [isDownloadingLogs, setIsDownloadingLogs] = React.useState(false);
  const downloadLogs = useDownloadLogs(connection);

  const handleClick = () => {
    history.push(buildRoute('', connection.index));
  };

  const handleMenuInteraction = (isOpening: boolean) => {
    if (isOpening) {
      validateConnection(connection);
    }
  };

  const handleEditMyEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.EDIT_ENDPOINT }));
  };

  const handleRenameSharedEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.RENAME_MY_SHARED_ENDPOINT }));
  };

  const handleManageUsers = () => {
    history.push(`connections/${connection.index}`);
  };

  const handleManageBilling = () => {
    getStripeSessionUrl({});
  };

  const handleDiagnostic = () => {
    history.push(buildRoute('', connection.index, 'diagnostic'));
  };

  const handleDownloadLogs = async () => {
    setIsDownloadingLogs(true);
    await downloadLogs();
    if (isMounted.current) {
      setIsDownloadingLogs(false);
    }
  };

  const handleDownloadVoiceboxReport = () => {
    dispatch(
      openedDialog({
        type: DialogType.VOICEBOX_RESPONSE_REPORT,
        data: { connection },
      })
    );
  };

  const handleDeleteMyEndpoint = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.DELETE_ENDPOINT }));
  };

  const handleDeleteSharedEndpoint = () => {
    withConfirmationDs({
      title:
        copy.components.endpoints.endpointTable.confirmations
          .deleteSharedEndpoint.title,
      message: copy.components.endpoints.endpointTable.confirmations.deleteSharedEndpoint.message(
        connection.name
      ),
      yesPrompt:
        copy.components.endpoints.endpointTable.confirmations
          .deleteSharedEndpoint.confirm,
      yesPromptTestId: PORTAL_ENDPOINT_TABLE_CONFIRMATION_YES,
      noPrompt:
        copy.components.endpoints.endpointTable.confirmations
          .deleteSharedEndpoint.cancel,
      onConfirmationSuccess: () => {
        if (!connection?.id) {
          return;
        }

        removeMySharedConnection({
          input: {
            connectionId: connection.id,
          },
        });
      },
    });
  };

  const handleCancelCloud = () => {
    dispatch(editConnection(connection.index));
    dispatch(openedDialog({ type: DialogType.CANCEL_ENDPOINT }));
  };

  const authType = getEndpointAuthType(connection);
  const isMyEndpoint =
    connection.organization?.owner &&
    connection.organization?.owner?.username === profile?.profile?.username;
  const isStardogCloudOrFree =
    connection.isStardogCloud || connection.isStardogFree;

  const actions = (
    <>
      {isMyEndpoint ? (
        <MenuItem
          leftIcon={null}
          onClick={handleEditMyEndpoint}
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM('edit-endpoint')}
          text={copy.components.endpoints.endpointTable.actions.editEndpoint}
        />
      ) : null}
      {!isMyEndpoint ? (
        <MenuItem
          leftIcon={null}
          onClick={handleRenameSharedEndpoint}
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'rename-shared-endpoint'
          )}
          text={
            copy.components.endpoints.endpointTable.actions.renameSharedEndpoint
          }
        />
      ) : null}
      {isMyEndpoint && isStardogCloudOrFree ? (
        <MenuItem
          leftIcon={null}
          onClick={handleManageUsers}
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM('manage-users')}
          text={copy.components.endpoints.endpointTable.actions.manageUsers}
        />
      ) : null}
      {isMyEndpoint && isStardogCloudOrFree ? (
        <MenuItem
          disabled={Boolean(connection.isAllocating)}
          leftIcon={null}
          onClick={handleManageBilling}
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM('manage-billing')}
          text={copy.components.endpoints.endpointTable.actions.manageBilling}
        />
      ) : null}
      <MenuItem
        leftIcon={null}
        onClick={handleDiagnostic}
        testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM('view-diagnostics')}
        text={copy.components.endpoints.endpointTable.actions.viewDiagnostics}
      />
      {isDownloadLogsSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadLogsPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs || isDownloadingLogs}
            leftIcon={null}
            onClick={handleDownloadLogs}
            testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-logs'
            )}
            text={copy.components.endpoints.endpointTable.actions.downloadLogs}
          />
        </Tooltip>
      ) : null}
      {isDownloadVoiceboxReportSupported ? (
        <Tooltip
          description={copy.components.endpoints.endpointTable.actions.downloadVoiceboxReportPermissionsTooltip(
            connection.name
          )}
          disabled={canReadLogs}
        >
          <MenuItem
            disabled={!canReadLogs}
            leftIcon={null}
            onClick={handleDownloadVoiceboxReport}
            testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
              'download-voicebox-report'
            )}
            text={
              copy.components.endpoints.endpointTable.actions
                .downloadVoiceboxReport
            }
          />
        </Tooltip>
      ) : null}
      {isSduSupported ? (
        <SduMenuActions
          canReadLogs={canReadLogs}
          connection={connection}
          downloadTestId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'download-sdu'
          )}
        />
      ) : null}
      {isMyEndpoint && isStardogCloudOrFree ? (
        <MenuItem
          leftIcon={null}
          onClick={handleCancelCloud}
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM('cancel-cloud')}
          text={copy.components.endpoints.endpointTable.actions.cancelCloud}
        />
      ) : null}
      {isMyEndpoint && !isStardogCloudOrFree ? (
        <MenuItem
          leftIcon={null}
          onClick={handleDeleteMyEndpoint}
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'delete-endpoint'
          )}
          text={copy.components.endpoints.endpointTable.actions.deleteEndpoint}
        />
      ) : null}
      {!isMyEndpoint ? (
        <MenuItem
          leftIcon={null}
          onClick={handleDeleteSharedEndpoint}
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU_ITEM(
            'delete-shared-endpoint'
          )}
          text={
            copy.components.endpoints.endpointTable.actions.deleteSharedEndpoint
          }
        />
      ) : null}
    </>
  );

  let authLabel;
  if (authType === 'org-sso') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.orgSSO;
  } else if (authType === 'sso') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.sso;
  } else if (authType === 'browser-auth') {
    authLabel = copy.components.endpoints.endpointTable.row.auth.browser;
  } else {
    authLabel = copy.components.endpoints.endpointTable.row.auth.username(
      connection.username || ''
    );
  }

  return (
    <>
      <td className="organization-table-row-endpoint-name">
        <Link
          onClick={handleClick}
          size="md"
          testId={PORTAL_ENDPOINT_TABLE_ROW_NAME(
            connection.id,
            connection.index
          )}
        >
          {connection.name}
        </Link>
      </td>
      <td className="organization-table-row-endpoint-url">
        <Body size="md">{getEndpointUrl(connection)}</Body>
      </td>
      <td className="organization-table-row-user-name">
        {isMyEndpoint ? null : (
          <div className="organization-table-row-user-name-cell">
            <div className="organization-table-row-user-name-title">
              <Body ellipsize size="md">
                {getUserInfo(connection?.organization?.owner)?.name}
              </Body>
            </div>
          </div>
        )}
      </td>
      <td className="organization-table-row-endpoint-auth">
        <Body size="md">{authLabel}</Body>
      </td>
      <td className="data-table-row-actions">
        <Menu
          leftIcon={OverflowMenuHorizontal}
          menu={
            <MenuItems className="data-table-menu-items">{actions}</MenuItems>
          }
          onInteraction={handleMenuInteraction}
          size="sm"
          testId={PORTAL_ENDPOINT_TABLE_ROW_ACTIONS_MENU(
            connection.id,
            connection.index
          )}
        />
      </td>
    </>
  );
};
